/**
 * The one place a name gets its " (n)" suffix: layouts, folders, profiles, strategies. Every
 * caller collects the names that are taken in its own scope (siblings, a flat list, …) and
 * passes them here.
 *
 * A name that already carries a generated suffix is re-numbered from its base, never suffixed
 * again: "Hello (1)" taken → "Hello (2)", not "Hello (1) (1)".
 *
 * Never throws: a bad argument is reported (console + PostHog) and the name comes back as-is,
 * so a save with a duplicated name beats a save that fails.
 */

import { captureException } from '@/services/analytics/use-analytics'

/** Trailing numbers above this are treated as part of the name ("Q4 (2024)"), not as a suffix. */
export const MAX_GENERATED_SUFFIX = 99

/** `"Hello (3)"` → `["Hello", 3]`; anything else → `[name, null]`. */
export function splitGeneratedSuffix(name: string): [base: string, n: number | null] {
  const match = /^(.*?) \((\d+)\)$/.exec(name)
  if (!match) return [name, null]
  const n = Number(match[2])
  if (n < 1 || n > MAX_GENERATED_SUFFIX) return [name, null]
  return [match[1], n]
}

/**
 * How a collision is resolved. `numbered` is the app-wide default ("Hello (1)"); `copy` is the
 * legacy alternative ("Hello Copy", then "Hello Copy Copy"), kept available but unused so the
 * whole app can switch in one place if it is ever preferred.
 */
export type UniqueNameStyle = 'numbered' | 'copy'

export interface UniqueNameOptions {
  /** Treat "hello" and "Hello" as the same name. */
  caseInsensitive?: boolean
  /** Defaults to `numbered`; anything unrecognised falls back to it. */
  style?: UniqueNameStyle
}

const COPY_SUFFIX = ' Copy'

/**
 * `desired` when nothing in `taken` uses it, otherwise `base (n)` with the lowest free n, where
 * `base` is `desired` without any generated suffix. Counting starts at 1 for a plain name and
 * just past the suffix for a numbered one, so a copy of "Hello (2)" reads "Hello (3)", never
 * "Hello (1)" and never "Hello (2) (1)" (the legacy site's generateProfileFilenameVersion did the
 * same bump, but parsed any trailing number, so "Q4 (2024)" became "Q4 (2025)"). An empty name
 * passes through untouched (unnamed layouts exist by design).
 */
export function uniqueName(
  desired: string,
  taken: Iterable<string>,
  opts: UniqueNameOptions = {},
): string {
  const fallback = desired == null ? '' : String(desired)
  try {
    if (typeof desired !== 'string') {
      throw new TypeError(`uniqueName: desired must be a string, got ${describe(desired)}`)
    }
    if (taken == null || typeof (taken as Iterable<string>)[Symbol.iterator] !== 'function') {
      throw new TypeError(`uniqueName: taken must be iterable, got ${describe(taken)}`)
    }
    const safeOpts = opts && typeof opts === 'object' ? opts : {}
    const style: UniqueNameStyle = safeOpts.style === 'copy' ? 'copy' : 'numbered'
    return uniqueNameStrict(desired, taken, Boolean(safeOpts.caseInsensitive), style)
  } catch (error) {
    report(error, desired, taken)
    return fallback
  }
}

function uniqueNameStrict(
  desired: string,
  taken: Iterable<string>,
  caseInsensitive: boolean,
  style: UniqueNameStyle,
) {
  if (desired === '') return desired
  const key = (s: string) => (caseInsensitive ? s.toLowerCase() : s)
  const used = new Set<string>()
  let skipped = 0
  for (const t of taken) {
    if (typeof t === 'string') used.add(key(t))
    else skipped++
  }
  if (skipped > 0) {
    report(new TypeError(`uniqueName: ${skipped} non-string entries in taken`), desired, taken)
  }
  if (!used.has(key(desired))) return desired
  if (style === 'copy') {
    let candidate = desired
    while (used.has(key(candidate))) candidate += COPY_SUFFIX
    return candidate
  }
  // Strip every generated suffix (legacy data holds names like "Hello (1) (1)") and count on
  // from the innermost one.
  let [base, current] = splitGeneratedSuffix(desired)
  for (let [b, n] = splitGeneratedSuffix(base); n != null; [b, n] = splitGeneratedSuffix(b)) {
    base = b
    current = n
  }
  for (let n = current == null ? 1 : current + 1; ; n++) {
    const candidate = `${base} (${n})`
    if (!used.has(key(candidate))) return candidate
  }
}

function describe(value: unknown): string {
  return value === null ? 'null' : typeof value
}

function report(error: unknown, desired: unknown, taken: unknown) {
  try {
    console.error('[unique-name] falling back to the name as given', error)
    captureException(error, { desiredType: describe(desired), takenType: describe(taken) })
  } catch {
    // Reporting must never be the thing that breaks a save.
  }
}
